import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined"
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined"
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ContactMail from "@mui/icons-material/ContactMail"
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined"
import DomainVerificationOutlinedIcon from "@mui/icons-material/DomainVerificationOutlined"
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined"
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined"
import MenuIcon from "@mui/icons-material/Menu"
import OpenInNew from "@mui/icons-material/OpenInNew"
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined"
import PinOutlinedIcon from "@mui/icons-material/PinOutlined"
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined"
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined"
import {
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Dialog,
  DialogTitle,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  useMediaQuery,
} from "@mui/material"
import MuiAppBar, { type AppBarProps as MuiAppBarProps } from "@mui/material/AppBar"
import Box from "@mui/material/Box"
import Container from "@mui/material/Container"
import CssBaseline from "@mui/material/CssBaseline"
import Divider from "@mui/material/Divider"
import MuiDrawer from "@mui/material/Drawer"
import IconButton from "@mui/material/IconButton"
import List from "@mui/material/List"
import { styled } from "@mui/material/styles"
import Toolbar from "@mui/material/Toolbar"
import Typography from "@mui/material/Typography"
import { useAppDispatch, useAppSelector } from "hooks"
import type React from "react"
import { useEffect, useRef, useState } from "react"
import { matchPath } from "react-router"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import authService from "services/authService"
import initPlausible from "utils/plausible"
import { resetChapter } from "slices/chapterSlice"
import { resetDevice } from "slices/deviceSlice"
import { resetLesson } from "slices/lessonSlice"
import { resetLicensePool } from "slices/licensePoolSlice"
import { resetLicense } from "slices/licenseSlice"
import { resetOrganization } from "slices/organizationSlice"
import { resetSimulatorApplication } from "slices/simulatorApplicationSlice"
import { resetTrainingModule } from "slices/trainingModuleSlice"
import { resetTrainingReport } from "slices/trainingReportSlice"
import { resetUserGroup } from "slices/userGroupSlice"
import { resetUsers, setCurrentUser } from "slices/usersSlice"
import { UserRoles } from "types/userRoles"
import HiabLogo from "../images/hiab_logo.svg"

const drawerWidth: number = 240
const selectedDrawerItemBackgroundColor = "#f0f0f0"
interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}))

export function Layout() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const isMobile = useMediaQuery("(max-width:1200px)")
  const [open, setOpen] = useState(true)
  const [roles, setRoles] = useState<Array<string>>()
  const [userNameLetters, setUserNameLetter] = useState<string>("")
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
  const [contactInfoOpen, setOpenContactInfo] = useState(false)

  const currentOrganization = useAppSelector((state) => state.organization.currentOrganization)

  const navigate = useNavigate()
  const plausibleInitialisedRef = useRef(false)

  useEffect(() => {
    setOpen(!isMobile)

    authService.getRolesAssignedToUser().then((roles) => {
      setRoles(roles)

      if (location.pathname === "/" && roles?.includes(UserRoles.HIAB_ADMIN)) {
        navigate("/organization")
      }
    })

    authService.getUserNameLetters().then((name) => {
      setUserNameLetter(name)
    })
  }, [isMobile, location.pathname, navigate])

  useEffect(() => {
    if (!roles || roles.length === 0) return
    if (plausibleInitialisedRef.current) return
    plausibleInitialisedRef.current = true

    const priorityRoles: string[] = [UserRoles.HIAB_ADMIN, UserRoles.ORGANIZATION_ADMIN, UserRoles.USER]
    const role = roles.find((r) => priorityRoles.includes(r)) ?? "unknown"
    initPlausible({ role })
  }, [roles])

  const toggleDrawer = () => {
    setOpen(!open)
  }

  const handleClickOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget)
  }

  const handleClickLogoutUser = () => {
    dispatch(resetOrganization(undefined))
    dispatch(resetDevice(undefined))
    dispatch(resetLicense(undefined))
    dispatch(resetLicensePool(undefined))
    dispatch(resetUserGroup(undefined))
    dispatch(resetUsers(undefined))
    dispatch(resetSimulatorApplication(undefined))
    dispatch(resetTrainingModule(undefined))
    dispatch(resetTrainingReport(undefined))
    dispatch(resetLesson(undefined))
    dispatch(resetChapter(undefined))
    dispatch(setCurrentUser(undefined))

    authService.logout()
    navigate("/login")
  }

  const handleClickCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  const hiabAdminListItems = roles?.includes(UserRoles.HIAB_ADMIN) && (
    <>
      <ListItemButton
        onClick={() => navigate("/organization")}
        style={
          matchPath({ path: "/organization" }, location.pathname)
            ? { backgroundColor: selectedDrawerItemBackgroundColor }
            : {}
        }
      >
        <ListItemIcon>
          <BusinessOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary="Organizations" />
      </ListItemButton>
      <ListItemButton
        onClick={() => navigate("/report")}
        style={
          matchPath({ path: "/report" }, location.pathname) ||
          matchPath({ path: "/report/growth" }, location.pathname)
            ? { backgroundColor: selectedDrawerItemBackgroundColor }
            : {}
        }
      >
        <ListItemIcon>
          <AssessmentOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary="Reports" />
      </ListItemButton>
      <ListItemButton
        onClick={() => navigate("/analytics")}
        style={
          matchPath({ path: "/analytics" }, location.pathname)
            ? { backgroundColor: selectedDrawerItemBackgroundColor }
            : {}
        }
      >
        <ListItemIcon>
          <QueryStatsOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary="Analytics" />
      </ListItemButton>
      <ListItemButton
        onClick={() => navigate("/simulator")}
        style={
          matchPath({ path: "/simulator" }, location.pathname)
            ? { backgroundColor: selectedDrawerItemBackgroundColor }
            : {}
        }
      >
        <ListItemIcon>
          <PrecisionManufacturingOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary="Simulators" />
      </ListItemButton>
      <Divider sx={{ my: 1 }} />
    </>
  )

  const organizationListItems = currentOrganization &&
    (roles?.includes(UserRoles.ORGANIZATION_ADMIN) || roles?.includes(UserRoles.HIAB_ADMIN)) && (
      <>
        <ListSubheader component="div" inset>
          {currentOrganization?.name}
        </ListSubheader>
        <ListItemButton
          onClick={() => navigate(`/organization/${currentOrganization.id}`)}
          style={
            matchPath({ path: "/organization/:id" }, location.pathname)
              ? { backgroundColor: selectedDrawerItemBackgroundColor }
              : {}
          }
        >
          <ListItemIcon>
            <HomeOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="My Organization" />
        </ListItemButton>
        <ListItemButton
          onClick={() => navigate(`/organization/${currentOrganization.id}/usergroup`)}
          style={
            matchPath(
              {
                path: "/organization/:id/usergroup",
              },
              location.pathname,
            )
              ? { backgroundColor: selectedDrawerItemBackgroundColor }
              : {}
          }
        >
          <ListItemIcon>
            <GroupOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="User Groups" />
        </ListItemButton>
        <ListItemButton
          onClick={() => navigate(`/organization/${currentOrganization.id}/user`)}
          style={
            matchPath(
              {
                path: "/organization/:id/user",
              },
              location.pathname,
            )
              ? { backgroundColor: selectedDrawerItemBackgroundColor }
              : {}
          }
        >
          <ListItemIcon>
            <PersonOutlineOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="Users" />
        </ListItemButton>
        <ListItemButton
          onClick={() => navigate(`/organization/${currentOrganization.id}/licensepool`)}
          style={
            matchPath(
              {
                path: "/organization/:id/licensepool",
              },
              location.pathname,
            )
              ? { backgroundColor: selectedDrawerItemBackgroundColor }
              : {}
          }
        >
          <ListItemIcon>
            <DomainVerificationOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="License Pools" />
        </ListItemButton>
        <ListItemButton
          onClick={() => navigate(`/organization/${currentOrganization.id}/device`)}
          style={
            matchPath(
              {
                path: "/organization/:id/device",
              },
              location.pathname,
            )
              ? { backgroundColor: selectedDrawerItemBackgroundColor }
              : {}
          }
        >
          <ListItemIcon>
            <DevicesOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary="Devices" />
        </ListItemButton>
        <Divider sx={{ my: 1 }} />
      </>
    )

  const userListItems = (roles?.includes(UserRoles.ORGANIZATION_ADMIN) ||
    roles?.includes(UserRoles.USER)) && (
    <>
      <ListItemButton
        onClick={() => navigate("/")}
        style={
          matchPath(
            {
              path: "/",
            },
            location.pathname,
          )
            ? { backgroundColor: selectedDrawerItemBackgroundColor }
            : {}
        }
      >
        <ListItemIcon>
          <SchoolOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary="My Training" />
      </ListItemButton>
      <ListItemButton
        onClick={() => navigate("/device")}
        style={
          matchPath(
            {
              path: "/device",
            },
            location.pathname,
          )
            ? { backgroundColor: selectedDrawerItemBackgroundColor }
            : {}
        }
      >
        <ListItemIcon>
          <PinOutlinedIcon />
        </ListItemIcon>
        <ListItemText primary="Device Login" />
      </ListItemButton>
    </>
  )

  const openContactInformation = () => {
    setOpenContactInfo(true)
  }

  const closeContactInformation = () => {
    setOpenContactInfo(false)
  }

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="absolute" open={isMobile ? open : false}>
        <Toolbar
          sx={{
            pr: "24px",
            backgroundColor: "#000000",
          }}
        >
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open drawer"
              onClick={toggleDrawer}
              sx={{
                marginRight: "36px",
                ...(open && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box sx={{ flexGrow: 0, marginTop: 0.67 }}>
            <HiabLogo height="25" />
          </Box>

          <Typography
            component="h1"
            variant="h3"
            color="inherit"
            noWrap
            marginLeft={1}
            sx={{ flexGrow: 1 }}
          >
            {!isMobile && "TRAINING SIMULATOR"}
          </Typography>
          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Open settings">
              <IconButton onClick={handleClickOpenUserMenu} sx={{ p: 0 }}>
                <Avatar sx={{ backgroundColor: "#D52B1E" }}>
                  <Typography component="h1" variant="h6">
                    {userNameLetters}
                  </Typography>
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleClickCloseUserMenu}
            >
              <MenuItem key="logout" onClick={handleClickLogoutUser}>
                <Typography textAlign="center">Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={isMobile ? open : true}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: [1],
          }}
        >
          {isMobile && (
            <IconButton onClick={toggleDrawer}>
              <ChevronLeftIcon />
            </IconButton>
          )}
        </Toolbar>
        <Divider />
        <List component="nav">
          {hiabAdminListItems}
          {organizationListItems}
          {userListItems}
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          backgroundColor: "#FFFFFF",
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
        }}
      >
        <Toolbar />
        <Container maxWidth="lg" sx={{ mt: 4, mb: 10 }}>
          <Outlet />
        </Container>
      </Box>
      <Paper sx={{ position: "fixed", bottom: 0, left: 0, right: 0 }} elevation={3}>
        <BottomNavigation showLabels={true}>
          <BottomNavigationAction
            label="Contact us"
            onClick={openContactInformation}
            icon={<ContactMail></ContactMail>}
          />
          <BottomNavigationAction
            label="Support"
            href="https://support.hiabtrainingsimulator.com/"
            target="_blank"
            icon={<OpenInNew></OpenInNew>}
          />
        </BottomNavigation>
      </Paper>
      <Dialog onClose={closeContactInformation} open={contactInfoOpen}>
        <DialogTitle>Contact information</DialogTitle>
        <Typography sx={{ padding: 4 }}>Email: training.simulator@digi.hiab.com</Typography>
      </Dialog>
    </Box>
  )
}
